import React, { useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";
import Checkbox from "../icons/NavCheckbox.tsx";
import {
  COLOR_APP_TEXT,
  COLOR_APP_TEXT_LIGHT,
  MOBILE_SCREEN,
} from "../theme.ts";
import { ParagraphHeaderXSmall } from "../typography/Paragraph.tsx";
import useIsMobile from "../../hooks/useIsMobile.ts";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";
import ErrorMessage from "../../components/Error.tsx";
import Loading from "../../components/Loading.tsx";
import {
  QuestionnaireSectionsForNavigation,
  QuestionnaireSectionStatus,
} from "../../../../api/src/types.ts";
import QuestionnairePopupManager from "../../pages/questionnaire/utils/QuestionnairePopupManager";
import useGetCurrentUserDocument from "../../hooks/useGetCurrentUserDocument";
import { useQuestionnaireSectionState } from "../../hooks/useQuestionnaireSectionState.ts";
import { useGetAnswersWithAttorneyRestrictionsQuery } from "../../api/answers.ts";
import { skipToken } from "@reduxjs/toolkit/query";
import { getSectionVisibility } from "../../pages/questionnaire/utils/index.ts";
import { useLocation } from "react-router";
import useCurrentUserPaymentData from "../../hooks/useCurrentUserPaymentData.ts";

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  gap: 8px;
  ${MOBILE_SCREEN} {
    margin-top: 0;
  }
`;

const SectionItem = styled.div`
  text-decoration: none;
  display: flex;
  flex-direction: row;
  gap: 8px;
  min-width: 216px;
  cursor: pointer;
  &:hover {
    color: ${COLOR_APP_TEXT};
    text-decoration-color: ${COLOR_APP_TEXT};
    text-decoration: underline;
  }
`;

const SectionItemCheckbox = styled.div`
  margin-left: 12px;
  min-width: 12px;
  ${MOBILE_SCREEN} {
    margin-left: 20px;
    min-width: 20px;
  }
`;

const SectionItemText = styled(ParagraphHeaderXSmall)<{ isActive: boolean }>`
  &:hover {
    color: ${COLOR_APP_TEXT};
  }
  ${(props) =>
    props.isActive &&
    `color: ${COLOR_APP_TEXT};
    `}
`;

const PaddedErrorMessage = styled(ErrorMessage)`
  padding-top: 16px;
  text-wrap: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PaddedLoading = styled(Loading)`
  padding-top: 16px;
`;

interface SectionListProps {
  sections: QuestionnaireSectionsForNavigation[];
  error: FetchBaseQueryError | SerializedError | string;
  loading: boolean;
  setIsExpanded?: (isExpanded: boolean) => void;
}

const SectionList: React.FC<SectionListProps> = ({
  sections,
  error,
  loading,
  setIsExpanded,
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    document,
    isLoading: isLoadingUserDocument,
    error: errorUserDocument,
  } = useGetCurrentUserDocument();
  const {
    currrentUserWillHireAttorney,
    currentUserRepresentationPurchase,
    isLoading: isLoadingQuestionnaireSectionState,
    error: errorQuestionnaireSectionState,
  } = useQuestionnaireSectionState();
  const { isPrenupPaid } = useCurrentUserPaymentData();

  const [showSectionIncompletePopup, setShowSectionIncompletePopup] =
    useState(false);
  const [selectedSection, setSelectedSection] =
    useState<QuestionnaireSectionsForNavigation | null>(null);

  const {
    data: answersWithAttorneyRestrictions,
    isLoading: isLoadingAnswersWithAttorneyRestrictions,
    isError: isErrorAnswersWithAttorneyRestrictions,
  } = useGetAnswersWithAttorneyRestrictionsQuery(
    user && document?.stateId && !isLoadingUserDocument && !errorUserDocument
      ? { userId: user.id }
      : skipToken,
  );

  const handleSectionValidationOnConfirm = () => {
    setShowSectionIncompletePopup(false);
    setSelectedSection(null);
    setIsExpanded?.(false);
  };

  const handleSectionValidationOnCancel = async () => {
    setShowSectionIncompletePopup(false);
    if (selectedSection) {
      await navigate(selectedSection.path);
    }
    setSelectedSection(null);
    setIsExpanded?.(false);
  };

  const handleSectionClick = async (
    section: QuestionnaireSectionsForNavigation,
  ) => {
    // Allow access to all sections from nav bar even if prenup is not paid
    // We have blocker in the questionnaire page to prevent access to section-groups if prenup is not paid
    if (!isPrenupPaid) {
      await navigate(section.path);
      return;
    }

    // Get the current section (where user is now)
    const currentSection = sections.find((s) => location.pathname === s.path);

    // If there's a current section and it's incomplete, show popup
    if (
      currentSection &&
      currentSection.status !== QuestionnaireSectionStatus.COMPLETED
    ) {
      // Only show popup if trying to navigate to a different section
      if (currentSection.path !== section.path) {
        setShowSectionIncompletePopup(true);
        setSelectedSection(section);
        return;
      }
    }
    if (isMobile) {
      setIsExpanded?.(false);
    }
    // Allow navigation if current section is completed or no current section
    await navigate(section.path);
  };

  const isLoading =
    loading ||
    isLoadingUserDocument ||
    isLoadingAnswersWithAttorneyRestrictions ||
    isLoadingQuestionnaireSectionState;
  const isError =
    error ||
    (errorUserDocument ?? isErrorAnswersWithAttorneyRestrictions) ||
    errorQuestionnaireSectionState;

  if (isError) {
    return (
      <PaddedErrorMessage>
        Error loading Questionnaire Sections
      </PaddedErrorMessage>
    );
  }

  if (isLoading) {
    return <PaddedLoading />;
  }

  return (
    <>
      {showSectionIncompletePopup && (
        <QuestionnairePopupManager
          validationMessagePopupOpen={showSectionIncompletePopup}
          setValidationMessagePopupOpen={setShowSectionIncompletePopup}
          userDocument={document ?? undefined}
          isLastSection={false}
          onConfirm={handleSectionValidationOnConfirm}
          onCancel={handleSectionValidationOnCancel}
        />
      )}
      <SectionContainer>
        {sections.map((section) => {
          const isActive = location.pathname === section.path;
          const isCompleted =
            section.status === QuestionnaireSectionStatus.COMPLETED;
          const isSectionVisible = getSectionVisibility(
            sections,
            section,
            answersWithAttorneyRestrictions,
            document?.stateId ?? "",
          );

          const isDisabled =
            currrentUserWillHireAttorney &&
            !currentUserRepresentationPurchase &&
            !isSectionVisible;
          const sectionContent = (
            <>
              <SectionItemCheckbox>
                <Checkbox isChecked={isCompleted} />
              </SectionItemCheckbox>
              <SectionItemText
                marginLeft={isMobile ? "5px" : "6px"}
                color={COLOR_APP_TEXT_LIGHT}
                isActive={isActive}
              >
                {section.label}
              </SectionItemText>
            </>
          );

          return (
            <SectionItem
              key={section.label}
              onClick={() => !isDisabled && handleSectionClick(section)}
              style={
                isDisabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined
              }
            >
              {sectionContent}
            </SectionItem>
          );
        })}
      </SectionContainer>
    </>
  );
};

export default SectionList;
